// Toasts, badges, empty/error/loading states.
import { html, render } from '../utilities/html.js';
import { icon } from '../utilities/icons.js';

// ---- Toasts ----
let region;
function toastRegion() {
  if (!region || !document.body.contains(region)) {
    region = document.createElement('div');
    region.className = 'toast-region';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  return region;
}

export function toast(message, { type = 'info', timeout = 5000 } = {}) {
  const el = document.createElement('div');
  el.className = `toast toast--${type}`;
  const iconName = type === 'success' ? 'checkCircle' : type === 'error' ? 'alert' : 'info';
  render(el, html`${icon(iconName)}<div>${message}</div><button class="toast__close" type="button" aria-label="Dismiss">${icon('close', { size: 16 })}</button>`);
  el.querySelector('.toast__close').addEventListener('click', () => el.remove());
  toastRegion().appendChild(el);
  if (timeout) setTimeout(() => el.remove(), timeout);
  return el;
}

// ---- Badges ----
const STATUS_TONES = {
  active: 'success', accepted: 'success', succeeded: 'success', success: 'success', delivered: 'success', sent: 'success',
  pending: 'warning', queued: 'warning', suspended: 'warning', link_shared: 'info', retrying: 'warning',
  deactivated: 'danger', revoked: 'danger', failed: 'danger', denied: 'danger', expired: 'plain', failure: 'danger',
};
const STATUS_LABELS = { link_shared: 'Link shared', queued: 'Email queued' };

export function badge(label, tone = 'plain') {
  return html`<span class="badge badge--${tone}">${label}</span>`;
}
export function statusBadge(status, labels = {}) {
  const label = labels[status] ?? STATUS_LABELS[status] ?? String(status ?? '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  return badge(label, STATUS_TONES[status] ?? 'plain');
}

// ---- States ----
export function emptyState({ title, message = '', iconName = 'inbox', action = '' }) {
  return html`<div class="empty-state">
    <div class="empty-state__icon">${icon(iconName)}</div>
    <h3>${title}</h3>${message ? html`<p>${message}</p>` : ''}${action}
  </div>`;
}

export function errorState({ title = 'Couldn’t load this', message = 'Something went wrong while loading.', retryLabel = 'Try again' } = {}) {
  return html`<div class="error-state" role="alert">
    <div class="error-state__icon">${icon('alert')}</div>
    <h3>${title}</h3><p>${message}</p>
    <button class="btn" type="button" data-action="retry">${icon('refresh')}${retryLabel}</button>
  </div>`;
}

export function renderError(target, error, onRetry) {
  render(target, errorState({ message: error?.message ?? 'Something went wrong.' }));
  target.querySelector('[data-action="retry"]')?.addEventListener('click', onRetry);
}

export function skeletonRows(rows = 5) {
  return html`<div class="card__body stack" aria-busy="true" aria-label="Loading">
    ${Array.from({ length: rows }, (_, i) => html`<span class="skeleton skeleton--text" style="width:${90 - (i % 3) * 18}%"></span>`)}
  </div>`;
}

export function pageSkeleton() {
  return html`<div aria-busy="true" aria-label="Loading page">
    <div class="page-header"><div class="stack stack--sm" style="width:min(420px,100%)"><span class="skeleton skeleton--title"></span><span class="skeleton skeleton--text" style="width:70%"></span></div></div>
    <div class="grid grid--stats">${Array.from({ length: 3 }, () => html`<div class="card stat-card"><span class="skeleton skeleton--text" style="width:50%"></span><span class="skeleton skeleton--title mt-4" style="width:35%"></span></div>`)}</div>
    <div class="card mt-6">${skeletonRows(6)}</div>
  </div>`;
}

export function alertBox(type, title, message) {
  const iconName = type === 'danger' ? 'alert' : type === 'success' ? 'checkCircle' : 'info';
  return html`<div class="alert alert--${type}" role="${type === 'danger' ? 'alert' : 'status'}">${icon(iconName)}<div>${title ? html`<strong>${title}</strong>` : ''}<p>${message}</p></div></div>`;
}
